import { useEffect, useRef } from 'react';

import { useUser } from '@clerk/react';

import { VIEW_TYPES } from '../enums/viewTypes';
import { useGetUserCommunicationById } from '../services/userCommunications.service';
import { useUserCommunicationsStore } from '../store';
import { sweepOrphanSnapshots } from '../utils/composeSnapshotSync';
import { COMPOSE_SNAPSHOT_SWEEP_THROTTLE_MS } from '../utils/constants/drafts';
import { VISIBILITY_VISIBLE } from '../utils/constants/uiConstants';

const SWEEP_EVENTS = {
  VISIBILITY_CHANGE: 'visibilitychange',
  FOCUS: 'focus',
} as const;

// Waits for the DRAFT view to sync, so a snapshot whose draft is on screen is never probed.
export const useOrphanSnapshotSweep = (): void => {
  const { user } = useUser();
  const userId = user?.id;
  const { getUserCommunicationById } = useGetUserCommunicationById();
  const fetchServerRowRef = useRef(getUserCommunicationById);
  fetchServerRowRef.current = getUserCommunicationById;
  const isDraftSynced = useUserCommunicationsStore((state) =>
    state.syncedViewTypes.includes(VIEW_TYPES.DRAFT),
  );

  useEffect(() => {
    if (!userId || !isDraftSynced) {
      return undefined;
    }
    let isDisposed = false;
    let isRunning = false;
    let lastRunAt = Number.NEGATIVE_INFINITY;

    const run = async () => {
      const isThrottled =
        Date.now() - lastRunAt < COMPOSE_SNAPSHOT_SWEEP_THROTTLE_MS;
      if (isRunning || isDisposed || isThrottled) {
        return;
      }
      isRunning = true;
      try {
        if (
          await sweepOrphanSnapshots(userId, {
            fetchServerRow: async (rowId) =>
              (await fetchServerRowRef.current(rowId)) ?? undefined,
          })
        ) {
          lastRunAt = Date.now();
        }
      } finally {
        isRunning = false;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === VISIBILITY_VISIBLE) {
        void run();
      }
    };

    void run();
    document.addEventListener(
      SWEEP_EVENTS.VISIBILITY_CHANGE,
      handleVisibilityChange,
    );
    window.addEventListener(SWEEP_EVENTS.FOCUS, run);
    return () => {
      isDisposed = true;
      document.removeEventListener(
        SWEEP_EVENTS.VISIBILITY_CHANGE,
        handleVisibilityChange,
      );
      window.removeEventListener(SWEEP_EVENTS.FOCUS, run);
    };
  }, [userId, isDraftSynced]);
};
